/**
 * TQ #337 (25.09.2026): "Rechnungen"-Abschnitt aus Billing.tsx extrahiert, damit
 * er sowohl im Personal-Mode (weiterhin inline in Billing.tsx) als auch im
 * Org-/Staff-Mode (jetzt als eigener Dokumente-Tab, /dokumente/rechnungen)
 * verwendet werden kann, ohne Logik zu duplizieren.
 *
 * Nachgezogen 30.09.2026 (Rebase auf main nach PR #100): Org-Mode liest jetzt
 * GET /api/org/:slug/erpnext/invoices (live aus ERPNext, ersetzt den seit
 * 03.06.2026 toten Sync-Pfad) statt der alten /invoices-Route. PDF-Ansicht
 * (Org-Mode) ueber GET /invoices/pdf (TQ #890), seit TQ #960 im gemeinsamen
 * PdfViewerModal (Knopf "Anzeigen", Vorschau + "PDF speichern"; 403 zeigt dort den
 * sachlichen forbiddenText-Hinweis statt eines Fehler-Toasts).
 * XRechnung-Export fuer Org-Rechnungen bleibt ungebaut. Personal-
 * Mode (Stripe-Rechnungen) ist von dieser Umstellung nicht betroffen.
 */
import { useEffect, useState } from 'react'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { useToast } from './ui'
import PdfViewerModal from './PdfViewerModal'
import { forbiddenText } from '../lib/forbiddenText'

interface PersonalInvoice {
  id: number | string
  invoice_number?: string
  amount?: number
  currency?: string
  status?: string
  issued_at?: string
  due_date?: string
  pdf_url?: string
  stripe_hosted_url?: string | null
}

// GET /api/org/:slug/erpnext/invoices (platform-workspace, 23.09.2026) — read-only
// live aus ERPNext (Sales Invoice, docstatus=1). Response-Shape ist eigenstaendig
// (kein erpnext_invoice_id/paid_amount/pdf_url), wird auf das gemeinsame
// Invoice-Shape gemappt, damit der bestehende Render-Pfad greift.
interface ErpInvoice {
  name: string
  status: string
  grand_total: number
  outstanding_amount: number
  currency: string
  posting_date: string
  due_date: string
}

interface Organization {
  id: number
  name: string
  slug: string
  public_token?: string
  member_role: string
}

interface Invoice {
  id: string | number
  erpnext_invoice_id?: string
  invoice_number?: string
  invoice_date?: string
  due_date?: string
  status?: string
  total_amount?: number
  outstanding_amount?: number
  paid_amount?: number
  currency?: string
  payment_date?: string
  pdf_url?: string
  stripe_invoice_id?: string
  stripe_hosted_url?: string
  stripe_status?: string
}

interface Props {
  organization: Organization
  personal: boolean
}

const fmtDate = (s?: string) =>
  s ? new Date(s).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '–'

// Betrag deutsch mit Waehrungscode, z. B. "1.428,00 EUR" (TQ #960). Waehrung aus den Daten,
// sonst EUR; ungueltiger Code -> Zahl deutsch formatiert plus Rohcode.
export function fmtAmount(n?: number, c?: string): string {
  if (n == null || Number.isNaN(n)) return '–'
  const currency = c?.trim() || 'EUR'
  try {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency, currencyDisplay: 'code' }).format(n)
  } catch {
    return `${n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`
  }
}

// ERPNext-Rechnungsstatus deutsch (TQ #960). Unbekannte Werte bleiben unveraendert.
const STATUS_DE: Record<string, string> = {
  'draft': 'Entwurf',
  'unpaid': 'Offen',
  'paid': 'Bezahlt',
  'overdue': 'Überfällig',
  'partly paid': 'Teilweise bezahlt',
  'partially paid': 'Teilweise bezahlt',
  'sent_via_stripe': 'Offen',
  'cancelled': 'Storniert',
  'return': 'Gutschrift',
  'credit note issued': 'Gutschrift erstellt',
  'unpaid and discounted': 'Offen (diskontiert)',
  'overdue and discounted': 'Überfällig (diskontiert)',
  'partly paid and discounted': 'Teilweise bezahlt (diskontiert)',
  'submitted': 'Gebucht',
  'internal transfer': 'Interne Umbuchung',
}

export function invoiceStatusLabel(status: string): string {
  return STATUS_DE[status.trim().toLowerCase()] ?? status
}

// Personal /api/invoices → org Invoice-Shape (Feld-Mapping issued_at→invoice_date,
// amount→total_amount), damit der gemeinsame Render-Pfad greift (TQ #6930).
function normalizePersonalInvoice(p: PersonalInvoice): Invoice {
  return {
    id: p.id,
    invoice_number: p.invoice_number,
    invoice_date: p.issued_at,
    due_date: p.due_date,
    status: p.status,
    total_amount: p.amount,
    currency: p.currency,
    pdf_url: p.pdf_url,
    stripe_hosted_url: p.stripe_hosted_url ?? undefined,
  }
}

function normalizeErpInvoice(inv: ErpInvoice): Invoice {
  return {
    id: inv.name,
    invoice_number: inv.name,
    invoice_date: inv.posting_date,
    due_date: inv.due_date,
    status: inv.status,
    total_amount: inv.grand_total,
    outstanding_amount: inv.outstanding_amount,
    currency: inv.currency,
  }
}

export default function InvoicesSection({ organization, personal }: Props) {
  const { api: keycloakApi } = useAuth()
  const { showToast } = useToast()

  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [loadingInvoices, setLoadingInvoices] = useState(true)
  // PDF-Viewer-Modal (TQ #960): aktuell angezeigte Rechnung (null = Modal zu).
  const [viewerInvoice, setViewerInvoice] = useState<Invoice | null>(null)

  // Belegnummer fuer GET /invoices/pdf?id= (wie TQ #890): Org-Mode mappt ErpInvoice.name auf
  // id und invoice_number (normalizeErpInvoice), erpnext_invoice_id ist dort nie gesetzt.
  const invoiceId = (inv: Invoice) => inv.invoice_number || String(inv.id)

  // Rechnungsansicht (PDF) aus ERPNext (GET /invoices/pdf?id=, Print Format ohne XRechnung).
  // Belegnummer als Query-Param wegen '/' im Format (sce00026-12/25). Fehler (auch 403)
  // zeigt das Modal selbst an.
  async function loadInvoicePdf(inv: Invoice): Promise<Blob> {
    const res = await keycloakApi!.get(
      `/api/org/${organization.public_token || organization.slug}/invoices/pdf`,
      { responseType: 'blob', params: { id: invoiceId(inv) } }
    )
    return res.data as Blob
  }

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoadingInvoices(true)
      try {
        const url = personal
          ? '/api/invoices'
          : `/api/org/${organization.public_token || organization.slug}/erpnext/invoices`
        const res = await keycloakApi!.get(url)
        if (cancelled) return
        const raw = res.data?.invoices || res.data?.data || res.data || []
        const list = personal
          ? (Array.isArray(raw) ? raw.map(normalizePersonalInvoice) : [])
          : (Array.isArray(raw) ? raw.map(normalizeErpInvoice) : [])
        setInvoices(Array.isArray(list) ? list : [])
      } catch (e: unknown) {
        if (cancelled) return
        const err = e as { response?: { status?: number; data?: { error?: string } } }
        const status = err.response?.status
        if (status !== 404) {
          showToast('error', forbiddenText(e) ?? `Rechnungen konnten nicht geladen werden: ${err.response?.data?.error ?? 'Unbekannt'}`)
        }
        setInvoices([])
      } finally {
        if (!cancelled) setLoadingInvoices(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [personal, (organization.public_token || organization.slug), keycloakApi, showToast])

  return (
    <>
      <section className="settings-section" style={{ marginBottom: '1.5rem' }}>
        <div className="section-header">
          <h2>📑 Rechnungen</h2>
          <span className="section-badge">{invoices.length}</span>
        </div>
        {personal && (
          <p style={{ margin: '0 0 0.875rem', color: '#6b7280', fontSize: '0.8125rem' }}>
            Deine Rechnungen — Online-Zahlung und Download über Stripe.
          </p>
        )}

        {loadingInvoices ? (
          <p style={{ color: '#6b7280' }}>Laden …</p>
        ) : invoices.length === 0 ? (
          <div style={{ padding: '1.25rem 1rem', background: '#f9fafb', borderRadius: 6, color: '#6b7280', textAlign: 'center' }}>
            {personal
              ? 'Du hast noch keine Rechnungen. Sobald die erste Abrechnungsperiode endet, erscheint hier deine Rechnung.'
              : 'Noch keine Rechnungen für diese Organisation.'}
            <div style={{ fontSize: '0.8125rem', marginTop: '0.5rem' }}>
              ERPNext Sales Invoices erscheinen hier sobald die ersten Subscriptions
              gebucht und abgerechnet wurden.
            </div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                  <th style={{ padding: '0.625rem 0.5rem' }}>Rechnung</th>
                  <th style={{ padding: '0.625rem 0.5rem' }}>Datum</th>
                  <th style={{ padding: '0.625rem 0.5rem' }}>Fällig</th>
                  <th style={{ padding: '0.625rem 0.5rem' }}>Status</th>
                  <th style={{ padding: '0.625rem 0.5rem', textAlign: 'right' }}>Betrag</th>
                  <th style={{ padding: '0.625rem 0.5rem' }}></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '0.625rem 0.5rem' }}>
                      <code style={{ fontSize: '0.8125rem' }}>{inv.invoice_number ?? inv.id}</code>
                    </td>
                    <td style={{ padding: '0.625rem 0.5rem' }}>{fmtDate(inv.invoice_date)}</td>
                    <td style={{ padding: '0.625rem 0.5rem' }}>{fmtDate(inv.due_date)}</td>
                    <td style={{ padding: '0.625rem 0.5rem' }}>
                      {inv.status ? (() => {
                        const s = inv.status.trim().toLowerCase()
                        const palette =
                          s === 'paid'                      ? { bg: '#d1fae5', fg: '#065f46' } :
                          s === 'overdue' || s === 'overdue and discounted' ? { bg: '#fee2e2', fg: '#991b1b' } :
                          s === 'unpaid' || s === 'sent_via_stripe' || s === 'partially paid' || s === 'partly paid'
                                                            ? { bg: '#ffedd5', fg: '#9a3412' } :
                                                              { bg: '#e5e7eb', fg: '#374151' }
                        return (
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: 4,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            background: palette.bg,
                            color: palette.fg,
                          }}>
                            {invoiceStatusLabel(inv.status)}
                          </span>
                        )
                      })() : '–'}
                    </td>
                    <td style={{ padding: '0.625rem 0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtAmount(inv.total_amount, inv.currency)}
                    </td>
                    <td style={{ padding: '0.625rem 0.5rem' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.8125rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {inv.stripe_hosted_url && inv.status?.toLowerCase() !== 'paid' && (
                          <a
                            href={inv.stripe_hosted_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Online bezahlen (Karte / SEPA-Lastschrift / PayPal über Stripe)"
                            style={{
                              padding: '4px 10px', border: 'none',
                              borderRadius: 4, background: '#635bff', color: '#fff',
                              cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 600,
                              textDecoration: 'none',
                            }}>
                            💳 Online bezahlen
                          </a>
                        )}
                        {/* Org-Mode: Rechnung im Anzeigen-Modal (TQ #890/#960).
                            Personal-Mode: kein ERPNext-Beleg → nur Stripe-PDF-Link
                            falls vorhanden (TQ #6930). */}
                        {personal ? (
                          (inv.stripe_hosted_url || inv.pdf_url) ? (
                            <a
                              href={(inv.stripe_hosted_url || inv.pdf_url) as string}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Rechnung bei Stripe öffnen / herunterladen"
                              style={{
                                padding: '4px 10px', border: '1px solid #d1d5db',
                                borderRadius: 4, background: '#fff', color: '#1f2937',
                                cursor: 'pointer', fontSize: '0.8125rem', textDecoration: 'none',
                              }}>
                              📄 Öffnen ↗
                            </a>
                          ) : null
                        ) : (
                          <button
                            type="button"
                            onClick={() => setViewerInvoice(inv)}
                            title="Rechnung als PDF anzeigen"
                            style={{
                              padding: '4px 10px', border: '1px solid #d1d5db',
                              borderRadius: 4, background: '#fff', color: '#1f2937',
                              cursor: 'pointer', fontSize: '0.8125rem',
                            }}>
                            📄 Anzeigen
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {viewerInvoice && (
        <PdfViewerModal
          key={String(viewerInvoice.id)}
          title={`Rechnung ${viewerInvoice.invoice_number ?? viewerInvoice.id}`}
          filename={invoiceId(viewerInvoice)}
          load={() => loadInvoicePdf(viewerInvoice)}
          onClose={() => setViewerInvoice(null)}
        />
      )}
    </>
  )
}
